import { useEffect, useRef } from 'react';

/**
 * Custom hook for Server-Sent Events (SSE) connection
 * @param {string} workerUrl - URL of the SSE worker endpoint
 * @param {Function} onMessage - Callback function called when a message is received
 * @param {Function} onError - Optional error callback
 * @returns {Object} - Object with connection status
 */
export function useSSE(workerUrl, onMessage, onError) {
  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;
  const reconnectDelay = 3000; // 3 seconds

  useEffect(() => {
    if (!workerUrl) {
      console.warn('SSE: Worker URL not provided');
      return;
    }

    const connect = () => {
      try {
        // Close existing connection if any
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
        }

        const eventSource = new EventSource(`${workerUrl}/events`);
        eventSourceRef.current = eventSource;

        eventSource.onopen = () => {
          console.log('SSE: Connected');
          reconnectAttemptsRef.current = 0;
        };

        eventSource.onmessage = (event) => {
          try {
            console.log('SSE: Message received:', event.data);
            const data = JSON.parse(event.data);
            if (onMessage) {
              onMessage(data);
            }
          } catch (error) {
            console.error('SSE: Error parsing message:', error);
            // If it's not JSON, it might be the initial "connected" message
            if (event.data === 'connected') {
              console.log('SSE: Connection established');
            }
          }
        };

        eventSource.onerror = (error) => {
          console.error('SSE: Connection error:', error);
          
          if (eventSource.readyState === EventSource.CLOSED) {
            // Connection closed, attempt to reconnect
            if (reconnectAttemptsRef.current < maxReconnectAttempts) {
              reconnectAttemptsRef.current += 1;
              console.log(`SSE: Reconnecting (attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})...`);
              
              reconnectTimeoutRef.current = setTimeout(() => {
                connect();
              }, reconnectDelay);
            } else {
              console.error('SSE: Max reconnection attempts reached');
              if (onError) {
                onError(new Error('Failed to reconnect to SSE server'));
              }
            }
          } else if (onError) {
            // Other errors
            onError(error);
          }
        };
      } catch (error) {
        console.error('SSE: Failed to create connection:', error);
        if (onError) {
          onError(error);
        }
      }
    };

    // Only connect if tab is visible
    if (!document.hidden) {
      connect();
    }

    // Handle visibility change
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab hidden, close connection
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
      } else {
        // Tab visible, reconnect
        if (!eventSourceRef.current || eventSourceRef.current.readyState === EventSource.CLOSED) {
          reconnectAttemptsRef.current = 0;
          connect();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup on unmount
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  }, [workerUrl, onMessage, onError]);
}

/**
 * Get the worker URL from environment variable or construct from current origin
 * @returns {string|null} - Worker URL or null if not available
 */
export function getWorkerUrl() {
  // Check for explicit environment variable
  if (import.meta.env.VITE_WORKER_URL) {
    return import.meta.env.VITE_WORKER_URL;
  }

  // In production, if worker is deployed as a separate service,
  // you'll need to set VITE_WORKER_URL in your environment variables
  // For now, return null to gracefully handle missing URL
  return null;
}
